import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../Modal';
import { DuplicateClientAlert } from '../DuplicateClientModal';
import { DuplicateClientError, type ExistingClientRef } from '../../api/clients';
import { SortableTh } from '../SortableTh';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import {
  convertWebsiteFormLead,
  fetchWebsiteFormLeads,
  updateWebsiteFormLead,
  updateWebsiteFormLeadTags,
  websiteFormLeadName,
  LEAD_FIELD_TAG_SUGGESTIONS,
  LEAD_PRIORITY_TAGS,
  WEBSITE_FORM_LEAD_STATUSES,
  type WebsiteFormLeadRow,
  type WebsiteFormLeadStatus,
  type WebsiteFormLeadTag,
} from '../../api/websiteFormLeads';
import CampaignAttributionDetails from './CampaignAttributionDetails';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function truncate(value: string, max: number): string {
  if (!value) return '—';
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function tagsOf(lead: WebsiteFormLeadRow): WebsiteFormLeadTag[] {
  return Array.isArray(lead.tags) ? lead.tags : [];
}

function priorityOf(lead: WebsiteFormLeadRow): string {
  return tagsOf(lead).find((t) => t.kind === 'priority')?.label ?? '';
}

function fieldTagsOf(lead: WebsiteFormLeadRow): string[] {
  return tagsOf(lead)
    .filter((t) => t.kind === 'field')
    .map((t) => t.label);
}

/**
 * Older submissions stored company only inside the raw payload. Prefer the
 * dedicated column, then fall back so the confirmation modal still starts with
 * something real instead of the contact's name.
 */
function companyFromRawPayload(lead: WebsiteFormLeadRow): string {
  const raw = lead.raw_payload;
  if (!raw || typeof raw !== 'object') return '';
  for (const [key, value] of Object.entries(raw)) {
    if (/company|organization|organisation|business/i.test(key) && String(value).trim()) {
      return String(value).trim().slice(0, 255);
    }
  }
  return '';
}

/** Values the confirmation modal shows and the user may edit before saving. */
interface ConvertDraft {
  company: string;
  point_of_contact: string;
  contact_email: string;
  contact_phone: string;
  source: string;
  industry: string;
  notes: string;
}

function draftFromLead(lead: WebsiteFormLeadRow): ConvertDraft {
  const name = websiteFormLeadName(lead);
  const noteParts = [lead.message?.trim()].filter(Boolean) as string[];
  if (lead.area_of_interest?.trim()) noteParts.push(`Area of interest: ${lead.area_of_interest.trim()}`);
  if (lead.source?.trim()) noteParts.push(`Website form: ${lead.source.trim()}`);
  return {
    company: lead.company?.trim() || companyFromRawPayload(lead) || name,
    point_of_contact: name,
    contact_email: lead.email ?? '',
    contact_phone: lead.phone ?? '',
    source: lead.source?.trim() ? `Website — ${lead.source.trim()}` : 'Website Form',
    industry: fieldTagsOf(lead)[0] ?? lead.area_of_interest ?? '',
    notes: noteParts.join('\n\n'),
  };
}

interface Props {
  userId: number;
}

const WebsiteFormLeadsSection: React.FC<Props> = ({ userId }) => {
  const [leads, setLeads] = useState<WebsiteFormLeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | WebsiteFormLeadStatus>('Pending');
  const [selected, setSelected] = useState<WebsiteFormLeadRow | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [customTag, setCustomTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [rowBusyId, setRowBusyId] = useState<number | null>(null);

  const [converting, setConverting] = useState<WebsiteFormLeadRow | null>(null);
  const [convertDraft, setConvertDraft] = useState<ConvertDraft | null>(null);
  const [convertEditing, setConvertEditing] = useState(false);
  const [convertSaving, setConvertSaving] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [convertDuplicate, setConvertDuplicate] = useState<ExistingClientRef | null>(null);
  const [convertDone, setConvertDone] = useState<{ name: string; clientId: number } | null>(null);

  const { sortKey, sortDir, toggleSort } = useTableSortState();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setLeads(await fetchWebsiteFormLeads(userId));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load website form leads.');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const pendingCount = useMemo(() => leads.filter((l) => l.status === 'Pending').length, [leads]);

  const replaceLead = useCallback((updated: WebsiteFormLeadRow) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setSelected((prev) => (prev && prev.id === updated.id ? updated : prev));
  }, []);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== 'All' && l.status !== statusFilter) return false;
      if (!term) return true;
      return [
        websiteFormLeadName(l),
        l.email,
        l.phone,
        l.message,
        l.area_of_interest,
        l.source,
        l.notes,
        ...tagsOf(l).map((t) => t.label),
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [leads, search, statusFilter]);

  const sorted = useMemo(
    () =>
      sortRows(visible, sortKey, sortDir, (l, key) => {
        switch (key) {
          case 'name':
            return websiteFormLeadName(l);
          case 'email':
            return l.email;
          case 'interest':
            return l.area_of_interest;
          case 'priority':
            return priorityOf(l);
          case 'status':
            return l.status;
          case 'received':
            return l.created_at ?? '';
          default:
            return '';
        }
      }),
    [visible, sortKey, sortDir]
  );

  const openLead = (lead: WebsiteFormLeadRow) => {
    setSelected(lead);
    setNoteDraft(lead.notes ?? '');
    setCustomTag('');
    setSaveError('');
  };

  const applyUpdate = async (payload: { status?: WebsiteFormLeadStatus; notes?: string }) => {
    if (!selected) return;
    setSaving(true);
    setSaveError('');
    try {
      replaceLead(await updateWebsiteFormLead(selected.id, { user_id: userId, ...payload }));
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to update lead.');
    } finally {
      setSaving(false);
    }
  };

  /** Inline status change from the table, without opening the lead. */
  const changeStatus = async (lead: WebsiteFormLeadRow, status: WebsiteFormLeadStatus) => {
    setRowBusyId(lead.id);
    setLoadError('');
    try {
      replaceLead(await updateWebsiteFormLead(lead.id, { user_id: userId, status }));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to update status.');
    } finally {
      setRowBusyId(null);
    }
  };

  const saveTags = async (lead: WebsiteFormLeadRow, tags: WebsiteFormLeadTag[]) => {
    setSaving(true);
    setSaveError('');
    try {
      replaceLead(await updateWebsiteFormLeadTags(lead.id, userId, tags));
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to update tags.');
    } finally {
      setSaving(false);
    }
  };

  const togglePriority = (lead: WebsiteFormLeadRow, label: string) => {
    const others = tagsOf(lead).filter((t) => t.kind !== 'priority');
    const next: WebsiteFormLeadTag[] =
      priorityOf(lead) === label ? others : [...others, { kind: 'priority', label }];
    void saveTags(lead, next);
  };

  const toggleFieldTag = (lead: WebsiteFormLeadRow, label: string) => {
    const clean = label.trim();
    if (!clean) return;
    const current = tagsOf(lead);
    const has = current.some(
      (t) => t.kind === 'field' && t.label.toLowerCase() === clean.toLowerCase()
    );
    const next = has
      ? current.filter((t) => !(t.kind === 'field' && t.label.toLowerCase() === clean.toLowerCase()))
      : [...current, { kind: 'field' as const, label: clean }];
    void saveTags(lead, next);
  };

  const openConvert = (lead: WebsiteFormLeadRow) => {
    setConverting(lead);
    setConvertDraft(draftFromLead(lead));
    setConvertEditing(false);
    setConvertError('');
    setConvertDuplicate(null);
  };

  const setDraftField = <K extends keyof ConvertDraft>(key: K, value: ConvertDraft[K]) =>
    setConvertDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  const confirmConvert = async () => {
    if (!converting || !convertDraft) return;
    setConvertSaving(true);
    setConvertError('');
    setConvertDuplicate(null);
    try {
      const result = await convertWebsiteFormLead(converting.id, {
        user_id: userId,
        ...convertDraft,
      });
      replaceLead(result.lead);
      setConvertDone({ name: convertDraft.company, clientId: result.client_id });
      setConverting(null);
      setConvertDraft(null);
    } catch (e) {
      if (e instanceof DuplicateClientError) setConvertDuplicate(e.existing);
      else setConvertError(e instanceof Error ? e.message : 'Failed to add lead.');
    } finally {
      setConvertSaving(false);
    }
  };

  const renderTagPills = (lead: WebsiteFormLeadRow) => {
    const priority = priorityOf(lead);
    const fields = fieldTagsOf(lead);
    if (!priority && fields.length === 0) return <span className="website-lead-muted">—</span>;
    return (
      <span className="website-lead-tag-list">
        {priority ? (
          <span className={`website-lead-tag website-lead-tag--priority-${priority.toLowerCase()}`}>
            {priority}
          </span>
        ) : null}
        {fields.map((f) => (
          <span key={f} className="website-lead-tag">
            {f}
          </span>
        ))}
      </span>
    );
  };

  return (
    <section className="events-table-section" aria-labelledby="website-form-leads-heading">
      <h2 className="events-section-title" id="website-form-leads-heading">
        Website Form Leads <span className="events-section-count">({leads.length})</span>
        {pendingCount > 0 ? (
          <span className="website-lead-new-badge">{pendingCount} pending</span>
        ) : null}
      </h2>
      <p className="page-subtitle website-lead-intro">
        Submissions sent over from the public website forms. Approve one to add it to the client
        database as a lead.
      </p>

      <div className="website-lead-controls">
        <input
          type="search"
          className="website-lead-search"
          placeholder="Search name, email, phone, message, tags…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search website form leads"
        />
        <div className="website-lead-filters" role="group" aria-label="Filter by status">
          {(['All', ...WEBSITE_FORM_LEAD_STATUSES] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`website-lead-filter${statusFilter === s ? ' website-lead-filter--active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {convertDone ? (
        <p className="marketing-order-notice" role="status">
          {convertDone.name} was added to the client database and the sales team has been notified.{' '}
          <Link to={`/lead/${convertDone.clientId}`}>Open the lead</Link>.
        </p>
      ) : null}
      {loadError ? (
        <p className="create-order-error" role="alert">
          {loadError}
        </p>
      ) : null}

      {loading ? (
        <p className="page-subtitle">Loading website form leads…</p>
      ) : (
        <div className="events-table-wrap">
          <table className="tasks-search-results-table website-lead-table">
            <thead>
              <tr>
                <SortableTh label="Name" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Email" columnKey="email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Area of interest" columnKey="interest" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Tags" columnKey="priority" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Message</th>
                <SortableTh label="Status" columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Received" columnKey="received" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={8} className="events-empty-cell">
                    {leads.length === 0
                      ? 'No submissions yet. They appear here as soon as the website posts them.'
                      : 'No submissions match this filter.'}
                  </td>
                </tr>
              ) : (
                sorted.map((l) => (
                  <tr key={l.id} className="events-table-row-clickable" onClick={() => openLead(l)}>
                    <td data-label="Name">{websiteFormLeadName(l)}</td>
                    <td data-label="Email">{l.email || '—'}</td>
                    <td data-label="Area of interest">{l.area_of_interest || '—'}</td>
                    <td data-label="Tags">{renderTagPills(l)}</td>
                    <td data-label="Message">{truncate(l.message, 50)}</td>
                    <td data-label="Status" onClick={(e) => e.stopPropagation()}>
                      <select
                        className="website-lead-status-select"
                        value={l.status}
                        disabled={rowBusyId === l.id}
                        aria-label={`Status for ${websiteFormLeadName(l)}`}
                        onChange={(e) =>
                          void changeStatus(l, e.target.value as WebsiteFormLeadStatus)
                        }
                      >
                        {WEBSITE_FORM_LEAD_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td data-label="Received">{formatDateTime(l.created_at)}</td>
                    <td data-label="Actions" onClick={(e) => e.stopPropagation()}>
                      {l.converted_client_id != null ? (
                        <Link to={`/lead/${l.converted_client_id}`} className="website-lead-link">
                          View lead
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="website-lead-add-btn"
                          onClick={() => openConvert(l)}
                        >
                          Add to leads
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Lead detail: full message, tagging, status and internal notes. */}
      <Modal
        isOpen={selected != null}
        onClose={() => setSelected(null)}
        title={selected ? `Lead — ${websiteFormLeadName(selected)}` : 'Lead'}
        wide
      >
        {selected ? (
          <>
            <div className="modal-body">
              {saveError ? (
                <p className="create-order-error" role="alert">
                  {saveError}
                </p>
              ) : null}

              <dl className="website-lead-details">
                <div>
                  <dt>Name</dt>
                  <dd>{websiteFormLeadName(selected)}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    {selected.email ? <a href={`mailto:${selected.email}`}>{selected.email}</a> : '—'}
                  </dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>{selected.phone ? <a href={`tel:${selected.phone}`}>{selected.phone}</a> : '—'}</dd>
                </div>
                <div>
                  <dt>Company</dt>
                  <dd>{selected.company || '—'}</dd>
                </div>
                <div>
                  <dt>SMS marketing consent</dt>
                  <dd>{selected.sms_marketing_consent ? 'Yes' : 'No'}</dd>
                </div>
                <div>
                  <dt>Area of interest</dt>
                  <dd>{selected.area_of_interest || '—'}</dd>
                </div>
                <div>
                  <dt>Source</dt>
                  <dd>{selected.source || '—'}</dd>
                </div>
                <div>
                  <dt>Received</dt>
                  <dd>{formatDateTime(selected.created_at)}</dd>
                </div>
              </dl>

              <h4 className="website-lead-subheading">Message</h4>
              <p className="website-lead-message">{selected.message || '—'}</p>

              <CampaignAttributionDetails row={selected} />

              <h4 className="website-lead-subheading">Priority</h4>
              <div className="website-lead-tag-picker">
                {LEAD_PRIORITY_TAGS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    disabled={saving}
                    className={`website-lead-tag-btn${
                      priorityOf(selected) === p ? ' website-lead-tag-btn--on' : ''
                    }`}
                    onClick={() => togglePriority(selected, p)}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <h4 className="website-lead-subheading">Field</h4>
              <div className="website-lead-tag-picker">
                {Array.from(
                  new Set([...LEAD_FIELD_TAG_SUGGESTIONS, ...fieldTagsOf(selected)])
                ).map((f) => (
                  <button
                    key={f}
                    type="button"
                    disabled={saving}
                    className={`website-lead-tag-btn${
                      fieldTagsOf(selected).some((t) => t.toLowerCase() === f.toLowerCase())
                        ? ' website-lead-tag-btn--on'
                        : ''
                    }`}
                    onClick={() => toggleFieldTag(selected, f)}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="website-lead-tag-add">
                <input
                  type="text"
                  value={customTag}
                  placeholder="Add another field…"
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      toggleFieldTag(selected, customTag);
                      setCustomTag('');
                    }
                  }}
                />
                <button
                  type="button"
                  disabled={saving || !customTag.trim()}
                  className="website-lead-status-btn"
                  onClick={() => {
                    toggleFieldTag(selected, customTag);
                    setCustomTag('');
                  }}
                >
                  Add tag
                </button>
              </div>

              <h4 className="website-lead-subheading">Status</h4>
              <div className="website-lead-status-actions">
                {WEBSITE_FORM_LEAD_STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={saving || selected.status === s}
                    className={`website-lead-status-btn${
                      selected.status === s ? ' website-lead-status-btn--current' : ''
                    }`}
                    onClick={() => void applyUpdate({ status: s })}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <h4 className="website-lead-subheading">Client database</h4>
              {selected.converted_client_id != null ? (
                <p className="website-lead-muted">
                  Added as a lead.{' '}
                  <Link to={`/lead/${selected.converted_client_id}`}>Open the lead</Link>.
                </p>
              ) : (
                <button
                  type="button"
                  className="website-lead-add-btn"
                  onClick={() => {
                    setSelected(null);
                    openConvert(selected);
                  }}
                >
                  Add to leads
                </button>
              )}

              <h4 className="website-lead-subheading">Internal notes</h4>
              <textarea
                className="website-lead-notes"
                rows={4}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Follow-up notes for the team…"
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="cancel-button" onClick={() => setSelected(null)}>
                Close
              </button>
              <button
                type="button"
                className="update-button"
                disabled={saving || noteDraft === (selected.notes ?? '')}
                onClick={() => void applyUpdate({ notes: noteDraft })}
              >
                {saving ? 'Saving…' : 'Save notes'}
              </button>
            </div>
          </>
        ) : null}
      </Modal>

      {/* Confirmation before the submission becomes a lead in the client database. */}
      <Modal
        isOpen={converting != null}
        onClose={() => (convertSaving ? undefined : setConverting(null))}
        title="Add to client database"
        wide
      >
        {converting && convertDraft ? (
          <>
            <div className="modal-body">
              <p className="page-subtitle">
                {convertEditing
                  ? 'Adjust anything that needs cleaning up, then confirm.'
                  : 'Check the details below before adding this lead. Choose Edit to change anything.'}
              </p>
              {convertError ? (
                <p className="create-order-error" role="alert">
                  {convertError}
                </p>
              ) : null}
              {convertDuplicate ? <DuplicateClientAlert existing={convertDuplicate} /> : null}

              <div className="marketing-order-grid">
                <label className="marketing-order-field">
                  <span>Company *</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.company}
                      onChange={(e) => setDraftField('company', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.company || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Point of contact *</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.point_of_contact}
                      onChange={(e) => setDraftField('point_of_contact', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.point_of_contact || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Email</span>
                  {convertEditing ? (
                    <input
                      type="email"
                      value={convertDraft.contact_email}
                      onChange={(e) => setDraftField('contact_email', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.contact_email || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Phone</span>
                  {convertEditing ? (
                    <input
                      type="tel"
                      value={convertDraft.contact_phone}
                      onChange={(e) => setDraftField('contact_phone', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.contact_phone || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Source</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.source}
                      onChange={(e) => setDraftField('source', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.source || '—'}</p>
                  )}
                </label>
                <label className="marketing-order-field">
                  <span>Industry</span>
                  {convertEditing ? (
                    <input
                      type="text"
                      value={convertDraft.industry}
                      onChange={(e) => setDraftField('industry', e.target.value)}
                    />
                  ) : (
                    <p className="website-lead-readonly">{convertDraft.industry || '—'}</p>
                  )}
                </label>
              </div>
              <label className="marketing-order-field">
                <span>Notes</span>
                {convertEditing ? (
                  <textarea
                    rows={5}
                    value={convertDraft.notes}
                    onChange={(e) => setDraftField('notes', e.target.value)}
                  />
                ) : (
                  <p className="website-lead-message">{convertDraft.notes || '—'}</p>
                )}
              </label>
              <p className="website-lead-muted">
                Confirming marks this submission Approved and emails the sales team.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={() => setConverting(null)}
                disabled={convertSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="website-lead-status-btn"
                onClick={() => setConvertEditing((v) => !v)}
                disabled={convertSaving}
              >
                {convertEditing ? 'Done editing' : 'Edit'}
              </button>
              <button
                type="button"
                className="update-button"
                onClick={() => void confirmConvert()}
                disabled={
                  convertSaving ||
                  !convertDraft.company.trim() ||
                  !convertDraft.point_of_contact.trim()
                }
              >
                {convertSaving ? 'Adding…' : 'Confirm & add lead'}
              </button>
            </div>
          </>
        ) : null}
      </Modal>
    </section>
  );
};

export default WebsiteFormLeadsSection;
